


import {
  defineComponent,
  PropType,
  onMounted,
  computed,
  reactive,
} from '@vue/composition-api';
import type {
  EmergencyResponseTeamReportCar,
  EmergencyResponseTeamReportPhotoCount,
} from '@/models/externalReports/emergencyResponseTeam/spEmergencyResponseTeamReport';
import type { ExternalReportAbility } from '@/models/externalReports/externalReportAbility';
import type {
  EmergencyResponseTeamMtx,
} from '@/models/apis/externalReports/emergencyResponseTeam/emergencyResponseTeamMtxRequest';
import type { ImageCandidateBase as ImageCandidate } from '@/models/apis/image/imageRequest';
import type {
  EmergencyResponseTeamReportPhotoParams,
} from '@/models/apis/externalReports/emergencyResponseTeam/emergencyResponseTeamReportRequest';
import type {
  ExternalReportsLabelSp,
  ExternalReportSettings,
} from '@/models/apis/externalReports/external_report_settings';
import { useRequestGuard } from '@/composables/useRequestGuard';
import PhotoTaker from '@/components/lib/PhotoTaker.vue';
import emergencyResponseTeamReportApi from '@/apis/externalReports/emergencyResponseTeam/emergency_response_team_report';
import { getImageCandidate } from '@/lib/imageHelper';
import { dtFormat } from '@/lib/dateHelper';
import ConfirmModal from '@/components/lib/ConfirmModal.vue';

interface PhotoMemoState {
  photoType: string;
  photoCounts: EmergencyResponseTeamReportPhotoCount[];
  imageCandidates: ImageCandidate[];
  errorModalMsg: string;
  pendingPhotosCountBeforeSave: number;
  showCommonErrorModal: boolean;
  showSavePhotosConfirmModal: boolean;
  showRevertSavePhotosConfirmModal: boolean;
  showPhotoSaveErrorModal: boolean;
}

const MAX_PENDING_PHOTOS_COUNT = 8;
const MAX_SAVABLE_PHOTOS_COUNT = 50;

export default defineComponent({
  props: {
    emergencyResponseTeamCar: {
      type: Object as PropType<EmergencyResponseTeamReportCar>,
      required: true,
    },
    externalReportAbility: {
      type: Object as PropType<ExternalReportAbility>,
      required: true,
    },
    userSettings: {
      type: Object as PropType<ExternalReportSettings>,
      required: true,
    },
    emergencyResponseTeamMtxs: {
      type: Array as PropType<EmergencyResponseTeamMtx[]>,
      required: true,
    },
  },
  components: {
    PhotoTaker,
    ConfirmModal,
  },
  emits: [
    'revertScreen',
    'updateCommon',
    'showErrorOnCommon',
  ],
  setup(props, { emit }) {
    const state = reactive<PhotoMemoState>({
      photoType: '',
      photoCounts: [],
      imageCandidates: [],
      errorModalMsg: '',
      pendingPhotosCountBeforeSave: 0,
      showCommonErrorModal: false,
      showSavePhotosConfirmModal: false,
      showRevertSavePhotosConfirmModal: false,
      showPhotoSaveErrorModal: false,
    });
    const { doRequest, isRequesting } = useRequestGuard((errorMessage) => {
      emit('showErrorOnCommon', errorMessage);
    });

    const calculatePhotoCount = computed<number>(() => {
      const currentTypePhotoCount = state.photoCounts.find(item => item.photoType === state.photoType)?.count ?? 0;
      return currentTypePhotoCount + state.imageCandidates.length;
    });
    const photoTypes = computed(() => {
      return props.userSettings.choices.photo_types.map(e => {
        return {
          name: e,
          value: e,
        };
      });
    });
    const canSaveAdditionalPhoto = computed<boolean>(() => {
      return calculatePhotoCount.value <= MAX_SAVABLE_PHOTOS_COUNT;
    });
    const canAddPendingPhoto = computed<boolean>(() => {
      return state.imageCandidates.length < MAX_PENDING_PHOTOS_COUNT &&
        calculatePhotoCount.value < MAX_SAVABLE_PHOTOS_COUNT;
    });
    const canSavePhotos = computed<boolean>(() => {
      const hasImages = state.imageCandidates.length > 0;
      return !isRequesting.value &&
        canSaveAdditionalPhoto.value &&
        state.photoType !== '' &&
        hasImages;
    });

    const spLabels = computed<ExternalReportsLabelSp>(() => {
      return props.userSettings.sp;
    });
    const fileNamePrefixForDownload = computed<string>(() => {
      return `EmergencyResponseTeam_${spLabels.value.title.operation_name}_${state.photoType}`;
    });
    const pendingPhotosCount = computed<number>(() => {
      return state.imageCandidates.length;
    });

    const attachImageFile = async(file: File) => {
      if (!canSaveAdditionalPhoto.value) {
        state.errorModalMsg = '添付画像の最大数は' + MAX_SAVABLE_PHOTOS_COUNT + '枚です。';
        state.showCommonErrorModal = true;
        return;
      }

      const imageCandidate = await getImageCandidate(file);
      if (!imageCandidate) return;
      state.imageCandidates.push(imageCandidate);
    };
    const onImageFileAttach = async(evt: Event) => {
      const fileList = (evt.target as HTMLInputElement).files;
      if (!fileList || fileList.length === 0) return;
      const file = fileList[0];
      attachImageFile(file);
    };
    const removeImageCandidate = (idx: number) => {
      URL.revokeObjectURL(state.imageCandidates[idx].src);
      state.imageCandidates.splice(idx, 1);
    };
    const tryRevertEditImages = () => {
      if (state.imageCandidates.length === 0) {
        revertScreen();
        return;
      }
      state.showRevertSavePhotosConfirmModal = true;
    };
    const revertScreen = () => {
      emit('revertScreen');
    };
    const sendImagesInner = async() => {
      const emergencyResponseTeamReport = props.emergencyResponseTeamCar.report;
      const hasImages = state.imageCandidates.length > 0;
      if (emergencyResponseTeamReport.id === 0 || !hasImages) return;

      state.pendingPhotosCountBeforeSave = pendingPhotosCount.value;

      const mtxSlice = props.emergencyResponseTeamMtxs.slice(0);
      const mtxLength = mtxSlice.length;
      if (mtxLength === 0) {
        state.errorModalMsg = '位置情報が取得できません。再度操作を行ってください';
        state.showCommonErrorModal = true;
        return;
      }
      const lastMtx = mtxSlice.slice(mtxLength - 1, mtxLength)[0];

      let remainingImageCandidates: ImageCandidate[] = [];
      for (const [index, imageCandidate] of state.imageCandidates.entries()) {
        try {
          const reqObj: EmergencyResponseTeamReportPhotoParams = {
            ts: dtFormat(lastMtx.ts),
            lat: lastMtx.lat,
            lon: lastMtx.lon,
            photo_type: state.photoType,
            image: imageCandidate.file,
            comment: null,
          };
          await doRequest(async() => {
            await emergencyResponseTeamReportApi.addPhoto(emergencyResponseTeamReport.id, reqObj);
          });
          URL.revokeObjectURL(imageCandidate.src);
        } catch (e) {
          remainingImageCandidates = state.imageCandidates.slice(index);
          // 一つでも失敗したらユーザーに再送信を促す
          break;
        }
      }
      state.imageCandidates = remainingImageCandidates;
      if (state.imageCandidates.length === 0) {
        emit('updateCommon', props.userSettings.choices.status.working.key, props.emergencyResponseTeamCar.report);
      }
    };
    const sendImages = async() => {
      await sendImagesInner();

      // 写真の送信失敗処理
      if (state.imageCandidates.length > 0) {
        state.showSavePhotosConfirmModal = false;
        state.showPhotoSaveErrorModal = true;
        return;
      }
      state.showSavePhotosConfirmModal = false;
    };

    onMounted(async() => {
      await doRequest(async() => {
        state.photoCounts = await emergencyResponseTeamReportApi.getPhotoCount(props.emergencyResponseTeamCar.report.id);
      }, '写真の取得に失敗しました。再度操作を行ってください');
      state.photoType = photoTypes.value.length > 0 ? photoTypes.value[0].value : '作業状況';
    });

    return {
      state,
      canAddPendingPhoto,
      canSavePhotos,
      fileNamePrefixForDownload,
      isRequesting,
      spLabels,
      pendingPhotosCount,
      attachImageFile,
      onImageFileAttach,
      removeImageCandidate,
      revertScreen,
      sendImages,
      tryRevertEditImages,
    };
  },
});
